import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { 
  UserCheck, Search, Loader2, Eye, 
  Clock, CheckCircle2, XCircle, RefreshCw, Crown
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSubscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSub, setSelectedSub] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/organizer-subscriptions`);
      setSubscriptions(response.data);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      await axios.put(`${API_URL}/api/admin/organizer-subscriptions/${selectedSub.id}/approve`);
      toast.success('Subscription approved');
      fetchSubscriptions();
      setViewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessing(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/organizer-subscriptions/${selectedSub.id}/reject?reason=${encodeURIComponent(rejectionReason)}`
      );
      toast.success('Subscription rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      fetchSubscriptions();
      setViewDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { icon: Clock, label: 'Pending', className: 'bg-amber-100 text-amber-700' },
      approved: { icon: CheckCircle2, label: 'Approved', className: 'bg-green-100 text-green-700' },
      rejected: { icon: XCircle, label: 'Rejected', className: 'bg-red-100 text-red-700' }
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <Badge className={c.className}>
        <Icon className="h-3 w-3 mr-1" />
        {c.label}
      </Badge>
    );
  };

  const filteredSubs = subscriptions.filter(sub => {
    const matchesFilter = filter === 'all' || sub.status === filter;
    const matchesSearch = !searchTerm || 
      sub.organizer?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.organizer?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sub.plan?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const stats = {
    pending: subscriptions.filter(s => s.status === 'pending').length,
    approved: subscriptions.filter(s => s.status === 'approved').length,
    total: subscriptions.length
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-subscriptions-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-3">
              <UserCheck className="h-8 w-8 text-gold" />
              Organizer Subscriptions
            </h1>
            <p className="text-gray-400 mt-1">Approve or reject organizer subscription requests</p>
          </div>
          
          <Button 
            variant="outline" 
            onClick={fetchSubscriptions}
            className="gap-2 border-gray-700 text-gray-300 hover:bg-gray-800"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-amber-500">{stats.pending}</p>
              <p className="text-sm text-gray-400">Pending Approval</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-green-500">{stats.approved}</p>
              <p className="text-sm text-gray-400">Approved</p>
            </CardContent>
          </Card>
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-gray-400">{stats.total}</p>
              <p className="text-sm text-gray-400">Total Requests</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-4">
            <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
              <Tabs value={filter} onValueChange={setFilter}>
                <TabsList className="bg-gray-800">
                  <TabsTrigger value="all" className="data-[state=active]:bg-gray-700">All</TabsTrigger>
                  <TabsTrigger value="pending" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                    Pending ({stats.pending})
                  </TabsTrigger>
                  <TabsTrigger value="approved" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                    Approved
                  </TabsTrigger>
                  <TabsTrigger value="rejected" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
                    Rejected
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              <div className="relative w-full md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
                <Input
                  placeholder="Search organizers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-gray-800 border-gray-700 text-white"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="bg-gray-900 border-gray-800">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-gold" />
              </div>
            ) : filteredSubs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Crown className="h-12 w-12 mx-auto mb-3 text-gray-700" />
                <p>No subscription requests found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-gray-800 hover:bg-gray-800/50">
                    <TableHead className="text-gray-400">Organizer</TableHead>
                    <TableHead className="text-gray-400">Plan</TableHead>
                    <TableHead className="text-gray-400">Amount</TableHead>
                    <TableHead className="text-gray-400">Payment</TableHead>
                    <TableHead className="text-gray-400">Status</TableHead>
                    <TableHead className="text-gray-400">Date</TableHead>
                    <TableHead className="text-gray-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSubs.map((sub) => (
                    <TableRow key={sub.id} className="border-gray-800 hover:bg-gray-800/50">
                      <TableCell>
                        {sub.organizer && (
                          <div>
                            <p className="font-medium text-white">
                              {sub.organizer.first_name} {sub.organizer.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{sub.organizer.email}</p>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-indigo-500/20 text-indigo-400">
                          {sub.plan?.name || 'Unknown Plan'}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-bold text-gold">
                        {formatCurrency(sub.amount)}
                      </TableCell>
                      <TableCell className="uppercase text-xs text-gray-400">
                        {sub.payment_method || '-'}
                      </TableCell>
                      <TableCell>{getStatusBadge(sub.status)}</TableCell>
                      <TableCell className="text-gray-400 text-sm">
                        {formatDate(sub.submitted_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedSub(sub);
                            setViewDialogOpen(true);
                          }}
                          className="text-gray-400 hover:text-white hover:bg-gray-800"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-gold">Subscription Details</DialogTitle>
            <DialogDescription className="text-gray-400">
              Review and approve or reject this subscription request
            </DialogDescription>
          </DialogHeader>

          {selectedSub && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Organizer</p>
                  <p className="font-medium">
                    {selectedSub.organizer?.first_name} {selectedSub.organizer?.last_name}
                  </p>
                  <p className="text-xs text-gray-500">{selectedSub.organizer?.email}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Plan</p>
                  <Badge className="bg-indigo-500/20 text-indigo-400">
                    {selectedSub.plan?.name}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="font-bold text-gold text-lg">{formatCurrency(selectedSub.amount)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Payment Method</p>
                  <p className="font-medium uppercase">{selectedSub.payment_method}</p>
                </div>
              </div>

              {selectedSub.reference_number && (
                <div>
                  <p className="text-xs text-gray-500">Reference Number</p>
                  <p className="font-mono bg-gray-800 px-3 py-2 rounded">{selectedSub.reference_number}</p>
                </div>
              )}

              {selectedSub.payment_proof && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">Payment Proof</p>
                  <img 
                    src={selectedSub.payment_proof} 
                    alt="Payment Proof" 
                    className="rounded-lg max-h-48 w-full object-contain bg-gray-800"
                  />
                </div>
              )}

              <div className="flex items-center justify-between pt-4 border-t border-gray-800">
                <p className="text-xs text-gray-500">
                  Submitted: {formatDate(selectedSub.submitted_at)}
                </p>
                {getStatusBadge(selectedSub.status)}
              </div>

              {selectedSub.status === 'pending' && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleApprove}
                    disabled={processing}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  >
                    {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                    Approve
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setRejectDialogOpen(true)}
                    className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reject
                  </Button>
                </div>
              )}

              {selectedSub.status === 'rejected' && selectedSub.rejection_reason && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                  <p className="text-xs text-red-400 mb-1">Rejection Reason:</p>
                  <p className="text-red-300">{selectedSub.rejection_reason}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="max-w-md bg-gray-900 border-gray-800 text-white">
          <DialogHeader>
            <DialogTitle className="text-red-400">Reject Subscription</DialogTitle>
            <DialogDescription className="text-gray-400">
              Please provide a reason. The organizer will be notified.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g., Invalid payment proof, amount mismatch..."
              className="bg-gray-800 border-gray-700 text-white"
              rows={3}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setRejectDialogOpen(false)}
                className="flex-1 border-gray-700 text-gray-300"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={processing}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Confirm Rejection
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminSubscriptions;
